import React, { useContext, useEffect, useState } from 'react'
import { userService } from '../../services/user.service'
import { User } from '../../interfaces/definitions/user.interface'
import { Link } from 'react-router-dom'
import { LoadingContext } from '../../context/LoadingContext'
import { MdOutlineAdminPanelSettings } from 'react-icons/md'
import { FaUserCircle } from 'react-icons/fa'
import { Bounce } from 'react-activity'
import { FiArrowLeft, FiPlus } from 'react-icons/fi'
import EditUserModal from './EditUserModal'
import './AdminPage.css'
import { UserRole } from '../../interfaces/definitions/user-role.enum'

const roleOrder: UserRole[] = [
  UserRole.INCOMING,
  UserRole.IN_PROCESS,
  UserRole.OUTGOING,
  UserRole.CUSTOMER_SERVICE,
  UserRole.QUALITY_CONTROL
]

export const roleData = [
  { label: 'Incoming', value: UserRole.INCOMING },
  { label: 'In Process', value: UserRole.IN_PROCESS },
  { label: 'Outgoing', value: UserRole.OUTGOING },
  { label: 'Customer Service', value: UserRole.CUSTOMER_SERVICE },
  { label: 'Quality Control', value: UserRole.QUALITY_CONTROL }
]

interface HomeButtonProps {
  handleAlphabeticalOrder: () => void
  handleRoleOrder: () => void
  openModal: (user: User | undefined) => void
}

const HomeButton: React.FC<HomeButtonProps> = ({
  handleAlphabeticalOrder,
  handleRoleOrder,
  openModal
}) => {
  return (
    <div className='header-admin'>
      <Link to='/'>
        <button
          type='button'
          title='Go Back'
          className='header-button header-back-button'
        >
          <FiArrowLeft />
        </button>
      </Link>
      <div className='header-summary'>
        <h2 className='header-text' onClick={handleAlphabeticalOrder}>
          USERNAME
        </h2>
        <h2
          className='header-text'
          style={{
            width: '0',
            backgroundColor: 'transparent',
            cursor: 'default',
            color: 'transparent'
          }}
        />
        <h2 className='header-text' onClick={handleRoleOrder}>
          ROLE
        </h2>
      </div>
      <button
        onClick={() => openModal(undefined)}
        className='header-button add-user-button'
      >
        <FiPlus />
      </button>
    </div>
  )
}

const AdminPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([])
  const [fetching, setFetching] = useState<boolean>(false)
  const { isLoading, setIsLoading } = useContext(LoadingContext)
  const [userModalVisible, setUserModalVisible] = useState<boolean>(false)
  const [isAscendingUserName, setIsAscendingUserName] = useState(true)
  const [isAscendingRole, setIsAscendingRole] = useState(true)
  const [selectedUser, setSelectedUser] = useState<User | undefined>()

  useEffect(() => {
    setIsLoading(true)
  }, [])

  useEffect(() => {
    if (!isLoading) {
      return
    }
    try {
      const fetchUsers = async (): Promise<User[]> => {
        setFetching(true)
        const fetchedUsers = await userService.getUsers()
        setFetching(false)
        setIsLoading(false)
        return fetchedUsers.sort((a, b) =>
          a.username.localeCompare(b.username)
        )
      }
      void fetchUsers().then(setUsers)
    } catch (error) {
      console.error('🚀 ~ useEffect ~ error:', error)
    }
  }, [isLoading])

  useEffect(() => {
    console.log('users:', users)
  }, [users])

  useEffect(() => {
    if (users.length === 0 || fetching) {
      setIsLoading(true)
    } else {
      setIsLoading(false)
    }
  }, [users, fetching, setIsLoading])

  const handleToggleAlphabeticalOrder = (): void => {
    const sortedUsers = [...users].sort((a, b) => {
      return isAscendingUserName
        ? a.username.localeCompare(b.username)
        : b.username.localeCompare(a.username)
    })
    setUsers(sortedUsers)
    setIsAscendingUserName(!isAscendingUserName)
  }

  const handleToggleRoleOrder = (): void => {
    const sortedUsers = [...users].sort((a, b) => {
      const indexA = roleOrder.indexOf(a.role)
      const indexB = roleOrder.indexOf(b.role)
      return isAscendingRole ? indexA - indexB : indexB - indexA
    })
    setUsers(sortedUsers)
    setIsAscendingRole(!isAscendingRole)
  }

  const handleOpenModal = (user: User | undefined): void => {
    setSelectedUser(user)
    setUserModalVisible(true)
  }

  const handleCloseModal = (): void => {
    setSelectedUser(undefined)
    setUserModalVisible(false)
  }

  const handleSaveUser = (updatedUser: Partial<User>): void => {
    setUserModalVisible(false)
    const saveUser = async (): Promise<void> => {
      setIsLoading(true)
      try {
        if (selectedUser != null) {
          await userService.updateUser(
            selectedUser.id,
            updatedUser.role as UserRole,
            updatedUser.password as string,
            updatedUser.admin as boolean
          )
        } else {
          await userService.addUser(updatedUser as User)
        }
        handleCloseModal()
        // Refresh user list
        const refreshedUsers = await userService.getUsers()
        setUsers(refreshedUsers)
      } catch (error) {
        console.error('Error saving user:', error)
      } finally {
        setIsLoading(false)
      }
    }
    void saveUser()
  }

  const handleDeleteUser = (): void => {
    setUserModalVisible(false)
    const deleteUser = async (): Promise<void> => {
      if (selectedUser != null) {
        setIsLoading(true)
        try {
          await userService.deleteUser(selectedUser.id)
          handleCloseModal()
          // Refresh user list
          const refreshedUsers = await userService.getUsers()
          setUsers(refreshedUsers)
        } catch (error) {
          console.error('Error deleting user:', error)
        } finally {
          setIsLoading(false)
        }
      }
    }
    void deleteUser()
  }

  return (
    <>
      <div className='admin-page-container'>
        <HomeButton
          handleAlphabeticalOrder={handleToggleAlphabeticalOrder}
          handleRoleOrder={handleToggleRoleOrder}
          openModal={handleOpenModal}
        />
        <div className='container'>
          {isLoading
            ? (
              <Bounce color='#4285f4' size={50} className='users-loader' />
              )
            : (
                !(users.length === 0) &&
            !fetching &&
            users.map((user, index) => (
              <div
                key={index}
                className='item-container'
                onClick={() => handleOpenModal(user)}
                style={{ cursor: 'pointer' }}
              >
                <span className='username-text'>{user.username}</span>
                {user.admin
                  ? (
                    <div style={{ alignSelf: 'center' }}>
                      <MdOutlineAdminPanelSettings
                        className='admin-icon'
                        color={`var(--${user.role}-color)`}
                        size={30}
                      />
                    </div>
                    )
                  : (
                    <div style={{ alignSelf: 'center' }}>
                      <FaUserCircle
                        className='admin-icon'
                        color='lightgray'
                        size={30}
                      />
                    </div>
                    )}
                <div className='stepdiv'>
                  <span
                    className='step'
                    style={{ backgroundColor: `var(--${user.role}-color)` }}
                  >
                    {roleData
                      .find((step) => step.value === user.role)
                      ?.label.toLocaleUpperCase()}
                  </span>
                </div>
              </div>
            ))
              )}
        </div>
      </div>

      {userModalVisible && (
        <EditUserModal
          user={selectedUser}
          onClose={handleCloseModal}
          onSave={handleSaveUser}
          onDelete={handleDeleteUser}
        />
      )}
    </>
  )
}

export default AdminPage
